import type { ChatConfig } from '../types';
import { generateUUID } from '../utils';
import { ChatError } from './chatError';

/**
 * MessagingService handles all communication with the Salesforce messaging API
 * following the exact same structure as the reference implementation
 */
export class MessagingService {
  private config: ChatConfig;

  private jwt: string | null = null;

  private identityToken: string | null = null;

  private lastEventId: string | null = null;

  constructor(config: ChatConfig) {
    this.config = config;
  }

  /**
   * Send an HTTP request using fetch with specified parameters
   */
  private async sendFetchRequest(
    apiPath: string,
    method: string,
    requestHeaders?: Record<string, string>,
    requestBody?: any
  ): Promise<Response> {
    const headers = requestHeaders || {
      'Content-Type': 'application/json',
      ...(this.jwt && { Authorization: `Bearer ${this.jwt}` }),
    };

    const body = requestBody ? JSON.stringify(requestBody) : undefined;

    const response = await fetch(apiPath, {
      method,
      mode: 'cors',
      headers,
      ...(body && { body }),
    });

    if (response.status === 401) {
      // Unauthorized request. Clear the JWT.
      this.jwt = null;
    }

    if (!response.ok) {
      let responseObject;
      try {
        responseObject = Object.assign(response, await response.json());
      } catch (e) {
        responseObject = Object.assign(response, {
          message: `Error reading the body stream of error object: ${response}`,
        });
      }
      throw new ChatError(
        responseObject.message || 'Request failed',
        response.status,
        responseObject
      );
    }

    return response;
  }

  /**
   * Get a JWT for an anonymous user. This JWT is used for unauthenticated conversations.
   */
  async getUnauthenticatedAccessToken(): Promise<{
    accessToken: string;
    lastEventId: string;
    context?: any;
  }> {
    const { organizationId, deploymentDeveloperName, messagingUrl } =
      this.config;
    const capabilitiesVersion = '1';
    const platform = 'Web';
    const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/unauthenticated/access-token`;

    const response = await this.sendFetchRequest(
      apiPath,
      'POST',
      {
        'Content-Type': 'application/json',
      },
      {
        orgId: organizationId,
        esDeveloperName: deploymentDeveloperName,
        capabilitiesVersion,
        platform,
      }
    );

    const result = await response.json();
    this.jwt = result.accessToken;
    this.lastEventId = result.lastEventId;

    return result;
  }

  /**
   * Create a new conversation.
   */
  async createConversation(
    conversationId?: string,
    routingAttributes?: Record<string, string>
  ): Promise<void> {
    const { deploymentDeveloperName, messagingUrl } = this.config;
    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation`;

    await this.sendFetchRequest(apiPath, 'POST', undefined, {
      ...(routingAttributes && { routingAttributes }),
      esDeveloperName: deploymentDeveloperName,
      conversationId: conversationId || generateUUID(),
    });
  }

  /**
   * Get a JWT with the same subjectId but new clientId as the existing Messaging JWT.
   */
  async getContinuityJwt(): Promise<{ accessToken: string }> {
    const { messagingUrl } = this.config;
    const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/continuation-access-token`;

    const response = await this.sendFetchRequest(apiPath, 'GET');

    const result = await response.json();
    this.jwt = result.accessToken;

    return result;
  }

  /**
   * Get a list of all conversations the current subjectId is participating in.
   */
  async listConversations(includeClosedConversations = false): Promise<{
    openConversationsFound: number;
    closedConversationsFound: number;
    conversations: Array<{
      conversationId: string;
      startTimestamp: number;
      status: string;
    }>;
  }> {
    const { messagingUrl } = this.config;
    const limit = 20; // Default limit
    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/list?inclClosedConvs=${includeClosedConversations}&limit=${limit}`;

    const response = await this.sendFetchRequest(apiPath, 'GET');

    return response.json();
  }

  /**
   * Get a list of conversation entries for a given conversationId.
   */
  async listConversationEntries(
    conversationId: string,
    startTimestamp?: number,
    endTimestamp?: number,
    direction?: string
  ): Promise<
    Array<{
      conversationId: string;
      conversationEntry: any;
    }>
  > {
    const { messagingUrl } = this.config;
    const limit = 50; // Default limit
    const supportedEntryTypes = [
      'Message',
      'ParticipantChanged',
      'RoutingResult',
    ];

    const limitParam = `limit=${limit}`;
    const startTimestampParam = startTimestamp
      ? `&startTimestamp=${startTimestamp}`
      : '';
    const endTimestampParam = endTimestamp
      ? `&endTimestamp=${endTimestamp}`
      : '';
    const directionParam = direction ? `&direction=${direction}` : '';
    const entryTypeFilterParam = supportedEntryTypes
      ? `&entryTypeFilter=${supportedEntryTypes.join(',')}`
      : '';

    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entries?${limitParam}${startTimestampParam}${endTimestampParam}${directionParam}${entryTypeFilterParam}`;

    const response = await this.sendFetchRequest(apiPath, 'GET');

    const responseJson = await response.json();

    // Transform the response to look like a conversation entry received via a server-sent event
    const transformedData: Array<{
      conversationId: string;
      conversationEntry: any;
    }> = [];

    if (typeof responseJson !== 'object') {
      throw new Error(
        `Expected to receive JSON response, instead received ${responseJson}.`
      );
    }

    if (!Array.isArray(responseJson.conversationEntries)) {
      throw new Error(
        `Expected entries to be an Array, instead was: ${responseJson.conversationEntries}.`
      );
    }

    responseJson.conversationEntries.forEach((conversationEntry: any) => {
      conversationEntry.entryPayload = JSON.stringify(
        conversationEntry.entryPayload
      );
      transformedData.push({
        conversationId,
        conversationEntry,
      });
    });

    return transformedData;
  }

  /**
   * Publish a text message to a conversation.
   */
  async sendTextMessage(
    conversationId: string,
    text: string,
    messageId?: string,
    inReplyToMessageId?: string,
    isNewMessagingSession?: boolean,
    routingAttributes?: Record<string, string>,
    language?: string
  ): Promise<void> {
    const { messagingUrl, deploymentDeveloperName } = this.config;
    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/message`;

    await this.sendFetchRequest(apiPath, 'POST', undefined, {
      message: {
        ...(inReplyToMessageId && { inReplyToMessageId }),
        id: messageId || generateUUID(),
        messageType: 'StaticContentMessage',
        staticContent: {
          formatType: 'Text',
          text,
        },
      },
      ...(routingAttributes && { routingAttributes }),
      ...(isNewMessagingSession && { isNewMessagingSession }),
      esDeveloperName: deploymentDeveloperName,
      ...(language && { language }),
    });
  }

  /**
   * Publish a started/stopped typing indicator entry to a conversation.
   */
  async sendTypingIndicator(
    conversationId: string,
    typingIndicator: 'TypingStartedIndicator' | 'TypingStoppedIndicator'
  ): Promise<void> {
    const { messagingUrl } = this.config;
    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entry`;

    await this.sendFetchRequest(apiPath, 'POST', undefined, {
      entryType: typingIndicator,
      id: generateUUID(),
    });
  }

  /**
   * Close conversation and clean up JWT.
   */
  async closeConversation(conversationId: string): Promise<void> {
    const { messagingUrl, deploymentDeveloperName } = this.config;
    const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${deploymentDeveloperName}`;

    await this.sendFetchRequest(apiPath, 'DELETE');
  }

  /**
   * Authenticate with JWT token
   */
  async authenticate(jwt: string): Promise<void> {
    this.jwt = jwt;
  }

  /**
   * Set identity token for user verification
   * This method should be called after receiving an onEmbeddedMessagingReady event
   * and before starting a conversation to enable verified user conversations.
   */
  async setIdentityToken(identityToken: string): Promise<void> {
    this.identityToken = identityToken;
  }

  /**
   * Clear the current session including identity token
   * This method should be called when the user logs out to clear all messaging
   * and user data from all tabs and windows.
   */
  async clearSession(): Promise<void> {
    this.jwt = null;
    this.identityToken = null;
    this.lastEventId = null;
  }

  /**
   * Get an authenticated access token using the identity token
   * This replaces getUnauthenticatedAccessToken when user verification is enabled
   */
  async getAuthenticatedAccessToken(): Promise<{
    accessToken: string;
    lastEventId: string;
    context?: any;
  }> {
    if (!this.identityToken) {
      throw new ChatError(
        'Identity token is required for authenticated access',
        401,
        { message: 'No identity token set' }
      );
    }

    const { organizationId, deploymentDeveloperName, messagingUrl } =
      this.config;
    const capabilitiesVersion = '1';
    const platform = 'Web';
    const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/authenticated/access-token`;

    const response = await this.sendFetchRequest(
      apiPath,
      'POST',
      {
        'Content-Type': 'application/json',
      },
      {
        orgId: organizationId,
        esDeveloperName: deploymentDeveloperName,
        capabilitiesVersion,
        platform,
        authorizationType: 'JWT',
        customerIdentityToken: this.identityToken,
      }
    );

    const result = await response.json();
    this.jwt = result.accessToken;
    this.lastEventId = result.lastEventId;

    return result;
  }

  /**
   * Check if identity token is set (for user verification)
   */
  hasIdentityToken(): boolean {
    return !!this.identityToken;
  }

  async endConversation(conversationId: string): Promise<void> {
    return this.closeConversation(conversationId);
  }
}
